"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";

const navLinks = [
  { name: "Avaleht", href: "/" },
  { name: "Meist", href: "/meist" },
  { name: "Teenused", href: "/teenused" },
  { name: "Hinnakiri", href: "/hinnakiri" },
  { name: "Blogi", href: "/blog" },
  { name: "Tööd", href: "/tood" },
  { name: "Kontakt", href: "/kontakt" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[60] h-16 md:h-20 transition-all duration-300 ease-in-out ${
          scrolled || isOpen
            ? "bg-primary border-b border-accent/20"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-full flex items-center justify-between">
          {/* Logo Block */}
          <Link href="/" className="flex flex-col items-start group min-h-[44px] justify-center">
            <span className="font-heading font-extrabold text-2xl text-text-main tracking-tight leading-none group-hover:text-text-main/90 transition-colors">
              PPF Garage
            </span>
            <span className="text-text-muted text-[10px] uppercase tracking-[0.15em] mt-1 font-body">
              Premium Vehicle Protection
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6" aria-label="Peamine navigatsioon">
            {navLinks.map((link, index) => {
              const isActive = pathname === link.href;
              return (
                <div key={link.href} className="flex items-center gap-6">
                  <Link
                    href={link.href}
                    className={`font-body font-medium text-sm transition-colors duration-200 ${
                      isActive
                        ? "text-accent"
                        : "text-text-muted hover:text-text-main"
                    }`}
                  >
                    {link.name}
                  </Link>
                  {index < navLinks.length - 1 && (
                    <span className="text-white/15 text-lg leading-none select-none">
                      &middot;
                    </span>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex">
            <Link
              href="/kontakt"
              className="bg-accent text-primary font-heading font-bold px-6 py-2.5 rounded-sm hover:opacity-90 transition-opacity duration-200 text-sm tracking-wide"
            >
              Küsi pakkumist
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden text-text-main min-h-[44px] min-w-[44px] p-2 -mr-2"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        aria-hidden={!isOpen}
        className={`fixed inset-0 z-[70] bg-primary pt-16 md:pt-20 pb-6 px-4 md:px-6 flex flex-col md:hidden overflow-y-auto transition-all duration-300 ease-in-out ${
          isOpen ? "pointer-events-auto opacity-100 translate-y-0" : "pointer-events-none opacity-0 -translate-y-3"
        }`}
      >
        <nav className="flex flex-col gap-6 mt-8" aria-label="Mobiilne navigatsioon">
          {navLinks.map((link) => {
            const isActive = pathname === link.href;
            return (
              <Link
                key={link.href}
                href={link.href}
                className={`font-heading font-bold text-3xl transition-colors ${
                  isActive ? "text-accent" : "text-text-main"
                }`}
              >
                {link.name}
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto pt-10">
          <Link
            href="/kontakt"
            className="flex items-center justify-center bg-accent text-primary font-heading font-bold px-6 py-4 rounded-sm hover:opacity-90 transition-opacity text-lg w-full text-center tracking-wide"
          >
            Küsi pakkumist
          </Link>
        </div>
      </div>
    </>
  );
}
